/**
 * Arabic text normalization + fuzzy-variant query building for theme-side search.
 * Exposed as window.BF.arabic (no external dependencies).
 */
(function (window) {
  "use strict";

  // Equivalence classes keyed by their normalized representative character.
  // Used both to fold a query down to one canonical spelling (normalize)
  // and to re-expand a normalized token back into the glyphs a product
  // title in the Shopify index might actually contain (variants).
  var EQUIV_GROUPS = {
    ا: ["ا", "أ", "إ", "آ", "ٱ"],
    ي: ["ي", "ى", "ی", "ئ"],
    ك: ["ك", "ک"],
    ه: ["ه", "ة"],
    و: ["و", "ؤ"],
  };

  // Direct character folds applied during normalize().
  var FOLD_MAP = {
    أ: "ا",
    إ: "ا",
    آ: "ا",
    ٱ: "ا",
    ى: "ي",
    ی: "ي",
    ئ: "ي",
    ک: "ك",
    ة: "ه",
    ؤ: "و",
    // Persian consonants folded to their nearest Arabic equivalent.
    گ: "ك",
    ژ: "ز",
    پ: "ب",
    چ: "ج",
    // Lam-alef ligature forms.
    ﻻ: "لا",
    ﻼ: "لا",
    ﻹ: "لا",
    ﻺ: "لا",
    ﻷ: "لا",
    ﻸ: "لا",
    ﻵ: "لا",
    ﻶ: "لا",
  };

  // Arabic-Indic (U+0660-0669) and Extended/Persian Arabic-Indic (U+06F0-06F9) digits -> ASCII.
  var DIGIT_MAP = {
    "٠": "0",
    "١": "1",
    "٢": "2",
    "٣": "3",
    "٤": "4",
    "٥": "5",
    "٦": "6",
    "٧": "7",
    "٨": "8",
    "٩": "9",
    "۰": "0",
    "۱": "1",
    "۲": "2",
    "۳": "3",
    "۴": "4",
    "۵": "5",
    "۶": "6",
    "۷": "7",
    "۸": "8",
    "۹": "9",
  };

  var PUNCT_MAP = { "؟": "?", "،": ",", "؛": ";" };

  // Tashkeel/diacritics + tatweel + bidi + zero-width + BOM marks, stripped entirely.
  var STRIP_RE =
    /[\u0640\u064B-\u0655\u0670\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

  function buildReplaceRe(map) {
    return new RegExp("[" + Object.keys(map).join("") + "]", "g");
  }

  var FOLD_RE = buildReplaceRe(FOLD_MAP);
  var DIGIT_RE = buildReplaceRe(DIGIT_MAP);
  var PUNCT_RE = buildReplaceRe(PUNCT_MAP);

  /**
   * Fold Arabic spelling/diacritic variants + digits so fuzzy comparisons are stable.
   * @param {string} str
   * @returns {string}
   */
  function normalize(str) {
    if (!str) return "";
    var out = String(str).normalize("NFKC");
    out = out.replace(STRIP_RE, "");
    out = out.replace(FOLD_RE, function (ch) {
      return FOLD_MAP[ch];
    });
    out = out.replace(DIGIT_RE, function (ch) {
      return DIGIT_MAP[ch];
    });
    out = out.replace(PUNCT_RE, function (ch) {
      return PUNCT_MAP[ch];
    });
    out = out.replace(/\s+/g, " ").trim();
    return out.toLocaleLowerCase();
  }

  /**
   * Expand a single normalized token into likely raw-glyph spelling variants.
   * Bounded cartesian product over ambiguous character positions.
   * @param {string} token normalized token
   * @param {number} [cap] max variants to return (default 12)
   * @returns {string[]} always includes the raw token first
   */
  function variants(token, cap) {
    cap = cap || 12;
    if (!token) return [token];

    var positions = [];
    for (var i = 0; i < token.length; i++) {
      var ch = token[i];
      var group = EQUIV_GROUPS[ch];
      if (group && group.length > 1) {
        positions.push({ index: i, options: group });
      }
    }

    var results = [token];
    if (!positions.length) return results;

    var seen = {};
    seen[token] = true;

    // Vary one ambiguous position at a time first (cheap, high coverage),
    // then attempt limited multi-position combinations if the cap allows.
    for (var p = 0; p < positions.length && results.length < cap; p++) {
      var pos = positions[p];
      for (var o = 0; o < pos.options.length && results.length < cap; o++) {
        var variant =
          token.slice(0, pos.index) +
          pos.options[o] +
          token.slice(pos.index + 1);
        if (!seen[variant]) {
          seen[variant] = true;
          results.push(variant);
        }
      }
    }

    if (positions.length > 1 && results.length < cap) {
      (function combine(index, current) {
        if (results.length >= cap) return;
        if (index === positions.length) {
          if (!seen[current]) {
            seen[current] = true;
            results.push(current);
          }
          return;
        }
        var pos = positions[index];
        for (var o = 0; o < pos.options.length && results.length < cap; o++) {
          var next =
            current.slice(0, pos.index) +
            pos.options[o] +
            current.slice(pos.index + 1);
          combine(index + 1, next);
        }
      })(0, token);
    }

    return results.slice(0, cap);
  }

  /**
   * Rewrite a raw user query into a Shopify-search-friendly string with
   * OR'd spelling variants per token, so common Arabic spelling ambiguity
   * (hamza seats, alef maksura vs yeh, ta marbuta vs heh...) doesn't cause
   * missed matches. Falls back to the raw query if it's pure Latin or if
   * the variant expansion would exceed the safety cap.
   * @param {string} rawQuery
   * @param {{ tokenCap?: number, totalCap?: number }} [options]
   * @returns {string}
   */
  function buildQuery(rawQuery, options) {
    options = options || {};
    var tokenCap = options.tokenCap || 12;
    var totalCap = options.totalCap || 24;

    if (!rawQuery) return rawQuery;
    var trimmed = String(rawQuery).trim();
    if (!/[\u0600-\u06FF]/.test(trimmed)) return trimmed;

    var tokens = normalize(trimmed).split(" ").filter(Boolean);
    if (!tokens.length) return trimmed;

    var totalVariants = 0;
    var groups = [];
    for (var i = 0; i < tokens.length; i++) {
      var vs = variants(tokens[i], tokenCap);
      totalVariants += vs.length;
      if (totalVariants > totalCap) return trimmed;
      groups.push(vs.length > 1 ? "(" + vs.join(" OR ") + ")" : vs[0]);
    }

    return groups.join(" AND ");
  }

  /**
   * Priority-ordered, human-readable spelling alternates for a phrase — used
   * to retry a query that returned zero results, NOT to widen a single
   * request (Shopify's /search/suggest endpoint does not understand the
   * boolean syntax buildQuery() produces, so that function must never be
   * sent to it). Unlike normalize(), which folds toward the less common
   * catalog glyph for comparison purposes, this walks back up toward the
   * MORE common catalog spelling, most-likely-first, and always returns
   * plain phrases.
   * @param {string} rawQuery
   * @param {number} [cap] max alternates to return (default 2)
   * @returns {string[]} never includes rawQuery itself; [] if no Arabic present
   */
  function alternates(rawQuery, cap) {
    cap = cap || 2;
    var trimmed = String(rawQuery || "").trim();
    if (!/[؀-ۿ]/.test(trimmed)) return [];

    var norm = normalize(trimmed);
    var out = [];
    function push(candidate) {
      if (
        candidate &&
        candidate !== trimmed &&
        out.indexOf(candidate) === -1 &&
        out.length < cap
      ) {
        out.push(candidate);
      }
    }

    push(norm); // strips tashkeel/tatweel + folds hamza seats/digits/punctuation
    push(norm.replace(/ه(?=\s|$)/g, "ة")); // ta marbuta — most common Arabic spelling split
    push(norm.replace(/ي(?=\s|$)/g, "ى")); // alef maksura — second most common split
    push(norm.replace(/(^|\s)ا/g, "$1أ")); // hamzat qat' at word start

    return out.slice(0, cap);
  }

  /**
   * Heuristic relevance score for client-side re-ranking of returned results.
   * @param {string} normalizedTitle
   * @param {string} normalizedQuery
   * @returns {number} higher is more relevant
   */
  function score(normalizedTitle, normalizedQuery) {
    if (!normalizedTitle || !normalizedQuery) return 0;
    if (normalizedTitle === normalizedQuery) return 100;
    if (normalizedTitle.indexOf(normalizedQuery) === 0) return 80;
    if (normalizedTitle.indexOf(normalizedQuery) !== -1) return 60;

    var queryTokens = normalizedQuery.split(" ").filter(Boolean);
    if (!queryTokens.length) return 0;
    var titleTokens = normalizedTitle.split(" ").filter(Boolean);
    var matched = 0;
    for (var i = 0; i < queryTokens.length; i++) {
      if (titleTokens.indexOf(queryTokens[i]) !== -1) matched++;
    }
    if (matched === queryTokens.length) return 40;
    if (matched > 0) return Math.round((matched / queryTokens.length) * 20);
    return 0;
  }

  window.BF = window.BF || {};
  window.BF.arabic = {
    normalize: normalize,
    variants: variants,
    buildQuery: buildQuery,
    alternates: alternates,
    score: score,
    // variantFallback: fire the alternates() rescue when a query returns 0
    // products. Flip off once Search & Discovery synonyms / normalized tags
    // (see project notes) make the index itself orthography-tolerant.
    // logFallback: console.info() every rescue that finds results — this is
    // the worklist for which synonym pairs to add in Search & Discovery.
    config: { variantFallback: true, logFallback: true },
  };
})(window);
